"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * EX-QA02: the ONE accessible modal for progsity-web. Focus trap, Escape,
 * initial focus, focus restore, scroll lock, labelled title. Hand-rolled
 * because the repo carries no Radix dialog; keep every overlay on this.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Visually hide the title row (title still labels the dialog for AT). */
  hideTitle?: boolean;
  description?: string;
  children: ReactNode;
  /** Accessible label for the close button (localize at the call site). */
  closeLabel: string;
  /** Disable backdrop-click + Escape dismissal (e.g. while submitting). */
  dismissible?: boolean;
  /**
   * `EX-EDR03`: `dark` is the Judge's near-black, for a dialog opened from the
   * editor (the upload preview shows code on the editor's own theme), the
   * reason `SegmentedControl` and `Textarea` have the same tone (`docs/03` §6).
   */
  tone?: "light" | "dark";
  /**
   * `EX-SCB05`: `sheet` is a reading surface rather than a question: the whole
   * screen on a phone, a 920 px panel up to 640 px tall from `sm`, the title
   * row on its own line and the body a column whose children divide the
   * height (the standings' result popup, `docs/03` §6).
   *
   * `QZ-BLD03`: `bottom` is a panel that rises from the foot of a phone's
   * screen, full width with its top corners round and up to 85% of the
   * height, and the default panel from `sm` (the quiz builder's guide).
   */
  size?: "default" | "sheet" | "bottom";
  /** `EX-SCB05`: a mark drawn before the title, such as a problem's letter chip. */
  leading?: ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  hideTitle = false,
  description,
  children,
  closeLabel,
  dismissible = true,
  tone = "light",
  size = "default",
  leading,
  className,
}: DialogProps) {
  const dark = tone === "dark";
  const sheet = size === "sheet";
  const bottom = size === "bottom";
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const initial =
        panel.querySelector<HTMLElement>("[data-autofocus]") ??
        panel.querySelector<HTMLElement>(FOCUSABLE) ??
        panel;
      initial.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape" && dismissible) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    },
    [dismissible, onClose]
  );

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center",
        sheet ? "p-0 sm:p-4" : bottom ? "items-end p-0 sm:items-center sm:p-4" : "p-4"
      )}
      onKeyDown={handleKeyDown}
    >
      {/* No blur behind the panel: `AGENTS.md` rule 9, `backdrop-filter` drags on the low-end PCs contest halls run (`EX-EDR03`). */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50"
        onClick={dismissible ? onClose : undefined}
      />
      <div
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={cn(
          "relative z-10 w-full border shadow-xl",
          sheet
            ? "flex h-full flex-col overflow-hidden rounded-none sm:h-[min(640px,90vh)] sm:max-w-[920px] sm:rounded-2xl"
            : bottom
              ? "max-h-[85vh] overflow-y-auto rounded-b-none rounded-t-2xl p-5 sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl sm:p-6"
              : "max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl p-6",
          dark ? "border-white/10 bg-neutral-900 text-white/90" : "border-neutral-200 bg-white",
          className
        )}
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        <div
          className={cn(
            "flex items-start justify-between gap-4",
            sheet && cn("shrink-0 items-center border-b py-3 pl-4 pr-3 sm:pl-5", dark ? "border-white/10" : "border-neutral-200"),
            hideTitle && "sr-only"
          )}
        >
          {leading ? <div className="shrink-0">{leading}</div> : null}
          <div className={cn("min-w-0", sheet && "flex-1")}>
            <h2
              className={cn("text-lg font-bold", sheet && "truncate", dark ? "text-white" : "text-neutral-900")}
              id={titleId}
            >
              {title}
            </h2>
            {description ? (
              <p className={cn("mt-1 text-sm", dark ? "text-white/55" : "text-neutral-500")} id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          <button
            aria-label={closeLabel}
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors",
              dark
                ? "text-white/60 hover:bg-white/10 hover:text-white"
                : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
            )}
            onClick={onClose}
            type="button"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
        </div>
        <div className={cn(sheet ? "flex min-h-0 flex-1 flex-col" : !hideTitle && "mt-4")}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  /** Paints the confirm button red for destructive actions. */
  destructive?: boolean;
  pending?: boolean;
}

/** EX-QA04: replaces window.confirm on grading/authoring surfaces. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel,
  closeLabel,
  destructive = false,
  pending = false,
}: ConfirmDialogProps) {
  // A destructive dialog opens with focus on Cancel, so an Enter pressed out of
  // habit backs out instead of deleting (2026-09-24, the contest console).
  return (
    <Dialog closeLabel={closeLabel} dismissible={!pending} onClose={onClose} open={open} title={title}>
      {body ? <div className="text-sm text-neutral-600">{body}</div> : null}
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          className="inline-flex h-11 items-center justify-center rounded-xl border border-neutral-200 px-5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
          data-autofocus={destructive ? true : undefined}
          disabled={pending}
          onClick={onClose}
          type="button"
        >
          {cancelLabel}
        </button>
        <button
          className={cn(
            "inline-flex h-11 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white transition-colors disabled:opacity-60",
            destructive ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-primary/90"
          )}
          data-autofocus={destructive ? undefined : true}
          disabled={pending}
          onClick={onConfirm}
          type="button"
        >
          {pending ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
